import { useMemo, useState } from "react";
import { format, isValid, parse } from "date-fns";
import { DayPicker, type DateRange } from "react-day-picker";
import "react-day-picker/style.css";
import { CalendarRange } from "lucide-react";
import { Popover } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils";

const YMD = "yyyy-MM-dd";

function parseYmd(s: string | undefined): Date | undefined {
  if (!s?.trim()) return undefined;
  const d = parse(s.trim(), YMD, new Date());
  return isValid(d) ? d : undefined;
}

function labelFor(from?: string, to?: string): string {
  const df = parseYmd(from);
  const dt = parseYmd(to);
  if (df && dt) {
    return `${format(df, "MMM d, yyyy")} – ${format(dt, "MMM d, yyyy")}`;
  }
  if (df) return `From ${format(df, "MMM d, yyyy")}`;
  if (dt) return `Until ${format(dt, "MMM d, yyyy")}`;
  return "Created date range";
}

export interface DateRangePickerProps {
  from?: string;
  to?: string;
  onChange: (next: { from?: string; to?: string }) => void;
  className?: string;
  disabled?: boolean;
  id?: string;
}

export function DateRangePicker({
  from,
  to,
  onChange,
  className,
  disabled,
  id,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  const selected: DateRange | undefined = useMemo(() => {
    const a = parseYmd(from);
    const b = parseYmd(to);
    if (!a && !b) return undefined;
    return { from: a, to: b };
  }, [from, to]);

  const defaultMonth = useMemo(
    () => selected?.from ?? selected?.to ?? new Date(),
    [selected],
  );

  return (
    <div className={cn("min-w-[12rem] max-w-sm", className)}>
      <Popover
        isOpen={open}
        onToggle={() => {
          if (disabled) return;
          setOpen((o) => !o);
        }}
        side="bottom"
        align="start"
        className="p-3"
        trigger={
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className="h-10 w-full justify-start text-left font-normal"
          >
            <CalendarRange className="mr-2 h-4 w-4 shrink-0" />
            <span className="truncate">{labelFor(from, to)}</span>
          </Button>
        }
      >
        <DayPicker
          key={`${from ?? ""}|${to ?? ""}`}
          mode="range"
          defaultMonth={defaultMonth}
          numberOfMonths={1}
          selected={selected}
          onSelect={(range) => {
            if (!range) {
              onChange({});
              return;
            }
            if (!range.from) {
              onChange({});
              return;
            }
            const nf = format(range.from, YMD);
            const nt =
              range.to && isValid(range.to) ? format(range.to, YMD) : undefined;
            onChange({ from: nf, to: nt });
          }}
        />
        <div className="mt-2 flex justify-end border-t border-border pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange({});
              setOpen(false);
            }}
          >
            Clear
          </Button>
        </div>
      </Popover>
    </div>
  );
}
